class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = this.querySelector('#cart-notification');
    this.header = document.querySelector('[data-vh-header]');
    this.onBodyClick = this.handleBodyClick.bind(this);

    if (!this.notification) return;

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener('transitionend', () => {
      this.notification.focus();
      trapFocus(this.notification);
    }, { once: true });

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.notification.classList.remove('active');

    document.body.removeEventListener('click', this.onBodyClick);
    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const container = document.getElementById(
        section.containerId || section.id
      );
      const sectionHTML = parsedState.sections?.[section.id];

      if (!container || !sectionHTML) return;

      if (container.matches('[data-cart-icon-bubble]')) {
        this.updateCartIcon(container, sectionHTML);
        return;
      }

      const content = this.getSectionInnerHTML(sectionHTML, section.selector);

      if (content !== null) container.innerHTML = content;
    });

    if (typeof this.header?.reveal === 'function') this.header.reveal();
    this.open();
  }

  renderTurboItem(item, cart) {
    if (!item) return;

    const productContainer = this.querySelector('#cart-notification-product');
    const cartButton = this.querySelector('#cart-notification-button');
    const cartIcon = document.querySelector('[data-cart-icon-bubble]');
    const itemCount = Number(cart?.item_count ?? 0);

    this.productId = item.id || item.variant_id;

    if (productContainer) {
      const image = item.featured_image?.url || item.image || '';
      const title = item.product_title || item.title || '';
      const options = Array.isArray(item.options_with_values)
        ? item.options_with_values.filter((option) => {
            const name = String(option?.name || '').trim().toLowerCase();
            const value = String(option?.value || '').trim().toLowerCase();

            return !(
              name === 'title' &&
              value === 'default title'
            );
          })
        : [];

      productContainer.innerHTML = `
        <div class="cart-notification-product__inner">
          ${image ? `<img class="cart-notification-product__image" src="${image}" alt="" width="70" loading="lazy">` : ''}
          <div class="cart-notification-product__info">
            <h3 class="cart-notification-product__name h4">${this.escapeHTML(title)}</h3>
            ${options.map((option) => `
              <div class="cart-notification-product__option h4">
                <dt>${this.escapeHTML(option.name)}:</dt>
                <dd>${this.escapeHTML(option.value)}</dd>
              </div>
            `).join('')}
          </div>
        </div>
      `;
    }

    if (cartButton) {
      cartButton.textContent = `View cart (${itemCount})`;
    }

    if (cartIcon) {
      const badge = cartIcon.querySelector('[data-cart-count]');
      const label = cartIcon.querySelector('[data-cart-count-label]');

      if (badge) badge.textContent = itemCount;
      if (label) label.textContent = `${itemCount} item${itemCount === 1 ? '' : 's'} in cart`;
    }

    this.open();
  }

  async refreshCartDrawer() {
    const url = new URL(window.location.href);
    url.search = '';
    url.hash = '';
    url.searchParams.set('section_id', 'van-horn-header');

    try {
      const response = await fetch(url.toString(), {
        cache: 'no-store',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      if (!response.ok) {
        throw new Error(`Unable to refresh cart drawer: ${response.status}`);
      }

      const sectionHTML = await response.text();
      const container = document.getElementById('cart_reload');
      const content = this.getSectionInnerHTML(
        sectionHTML,
        '#cart_reload'
      );

      if (container && content !== null) {
        container.innerHTML = content;
      }
    } catch (error) {
      console.error(error);
    }
  }

  escapeHTML(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `#cart-notification-product-${this.productId}`,
      },
      {
        id: 'cart-notification-button'
      },
      {
        id: 'cart-icon-bubble'
      },
      {
        id: 'van-horn-header',
        containerId: 'cart_reload',
        selector: '#cart_reload'
      }
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector)?.innerHTML ?? null;
  }

  updateCartIcon(container, html) {
    const renderedSection = new DOMParser().parseFromString(html, 'text/html');
    const renderedBubble = renderedSection.querySelector('.cart-count-bubble');

    if (!renderedBubble) return;

    const count = renderedBubble.dataset.cartItemCount;
    const countLabel = renderedBubble.querySelector('.visually-hidden')?.textContent;
    const badge = container.querySelector('[data-cart-count]');
    const label = container.querySelector('[data-cart-count-label]');

    if (badge && count !== undefined) badge.textContent = count;
    if (label && countLabel) label.textContent = countLabel.trim();
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('vh-cart-notification')) {
      const disclosure = target.closest('details-disclosure');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('vh-cart-notification')) {
  customElements.define('vh-cart-notification', CartNotification);
}
